import React from 'react';
import { ProjectFormData, SelectedSubscription, RtdKonto } from './SubscriptionToProjectWizardTypes';

interface Step3Props {
    projectData: ProjectFormData;
    selectedSubscriptions: SelectedSubscription[];
    rtdKontos: RtdKonto[];
}

const SubscriptionToProjectWizardStep3: React.FC<Step3Props> = ({
    projectData,
    selectedSubscriptions,
    rtdKontos
}) => {
    const formatPrice = (cents: number) => {
        return `${(cents / 100).toFixed(2)} €`;
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'OPEN': return 'Offen';
            case 'CLOSED': return 'Geschlossen';
            case 'ARCHIVED': return 'Archiviert';
            default: return status;
        }
    };

    const selectedRtdKonto = rtdKontos.find(k => k.id === projectData.rtdKontoId);

    // Group subscriptions by Round Table
    const subscriptionsByRoundTable = selectedSubscriptions.reduce((acc, s) => {
        const rtId = s.subscription.ownerRoundTableId || 0;
        if (!acc[rtId]) {
            acc[rtId] = [];
        }
        acc[rtId].push(s.subscription);
        return acc;
    }, {} as Record<number, typeof selectedSubscriptions[0]['subscription'][]>);

    // Calculate total invoices (one per unique Round Table)
    const uniqueRoundTables = Object.keys(subscriptionsByRoundTable).length;

    return (
        <div className="subToProjWiz-wizard-step">
            <h3>Bestätigung</h3>
            <p className="subToProjWiz-step-description">
                Überprüfen Sie Ihre Auswahl vor der Erstellung.
            </p>

            <div className="subToProjWiz-confirmation-section">
                <h4>Projekt-Details</h4>
                <div className="subToProjWiz-confirmation-details">
                    <div className="subToProjWiz-detail-row">
                        <span className="subToProjWiz-detail-label">Name:</span>
                        <span className="subToProjWiz-detail-value">{projectData.name}</span>
                    </div>
                    {projectData.description && (
                        <div className="subToProjWiz-detail-row">
                            <span className="subToProjWiz-detail-label">Beschreibung:</span>
                            <span className="subToProjWiz-detail-value">{projectData.description}</span>
                        </div>
                    )}
                    <div className="subToProjWiz-detail-row">
                        <span className="subToProjWiz-detail-label">Status:</span>
                        <span className="subToProjWiz-detail-value">{getStatusLabel(projectData.status)}</span>
                    </div>
                    {selectedRtdKonto && (
                        <div className="subToProjWiz-detail-row">
                            <span className="subToProjWiz-detail-label">RTD Konto:</span>
                            <span className="subToProjWiz-detail-value">
                                {selectedRtdKonto.accountName} ({selectedRtdKonto.iban})
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="subToProjWiz-confirmation-section">
                <h4>Ausgewählte Abonnements</h4>
                <div className="subToProjWiz-confirmation-summary">
                    <p>
                        <strong>{selectedSubscriptions.length}</strong> Abonnement(e) ausgewählt
                    </p>
                    <p>
                        Es werden <strong>{uniqueRoundTables}</strong> Rechnung(en) erstellt
                        (eine pro Round Table)
                    </p>
                </div>

                <div className="subToProjWiz-subscription-groups">
                    {Object.entries(subscriptionsByRoundTable).map(([rtId, subs]) => {
                        const rtInfo = subs[0].ownerRoundTable;
                        const totalAmount = subs.reduce((sum, s) => sum + s.annualPrice, 0);

                        return (
                            <div key={rtId} className="subToProjWiz-round-table-group">
                                <div className="subToProjWiz-round-table-header">
                                    {rtInfo ? (
                                        <h5>RT {rtInfo.number} - {rtInfo.name}</h5>
                                    ) : (
                                        <h5>Kein Round Table zugeordnet</h5>
                                    )}
                                    <div className="subToProjWiz-group-summary">
                                        {subs.length} Abonnement(e) - Gesamt: {formatPrice(totalAmount)}
                                    </div>
                                </div>
                                <div className="subToProjWiz-subscription-group-list">
                                    {subs.map((sub) => (
                                        <div key={sub.id} className="subToProjWiz-subscription-summary-item">
                                            <span className="subToProjWiz-subscription-desc">
                                                {sub.description || 'Keine Beschreibung'}
                                            </span>
                                            <span className="subToProjWiz-subscription-amount">
                                                {formatPrice(sub.annualPrice)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="subToProjWiz-confirmation-warning">
                <strong>Hinweis:</strong> Beim Bestätigen wird das Projekt erstellt und
                für jeden Round Table eine Rechnung mit den entsprechenden Abonnements
                als Positionen angelegt.
            </div>
        </div>
    );
};

export default SubscriptionToProjectWizardStep3;
